// @flow

import React, { type Node } from 'react';
import { IfFeature } from '@tectonics/prs/feature';
import { getCountry, useIncomingPayment, getMerchant } from '@tectonics/prs/incoming-payment';
import { useCurrentReceipt } from '@tectonics/prs/receipts';
import { getReceiptAmountExt, getReceiptFx, getReceiptAmountLocalTotal } from '@tectonics/prs/receipts/receipt';
import { useAllFeatures } from '@tectonics/prs/payment-spec';
import { getName as getMerchantName, getMerchantLogo } from '@tectonics/prs/merchant';
import { translate } from '@tectonics/translate';

import {
  amountToCurrencyString,
  fxToCurrencyString,
} from '@tectonics/prs/money';

import { SidePanel } from '../../template';

import {
  SideDisplayValues,
  SideDisplayValueItem,
  SideDisplayValueItemTitle,
  SideDisplayValueItemValue,
} from './side-display-values';

import {
  SideDisplayTotal,
  SideDisplayTotalTitle,
  SideDisplayTotalValue,
} from './side-display-total';

import { MerchantLogoDisplay } from './merchant-logo-display';
import { Separator } from './separator';
import { DisplayBrazilianIOF } from './display-brazilian-iof';
import { DisplayArgentineIVA } from './display-argentine-iva';
import { DisplayArgentineIP } from './display-argentine-ip';
import { DisplayArgentineIGIBP } from './display-argentine-ig-ibp';
import { DisplayArgentineNonResidentIgbp } from './display-argentine-non-resident-igbp';
import { DisplayParaguayanIVA } from './display-paraguayan-iva';
import { DisplayCostaRicanIVA } from './display-costa-rican-iva';
import { DisplayElSalvadorianItbms } from './display-el-salvadorian-itbms';
import { DisplaySelectedInstalment } from './display-selected-instalment';
import { DisplayChileanRoundingDisclaimer } from './display-chilean-rounding-disclaimer';

import { getMoneyFormat } from '../../commons/money-format';
import { DisplayPayingWithEbanx } from './display-paying-with-ebanx';
import { DisplayArgentineIIBB } from './display-argentine-iibb';
import { DisplayCDPTransactionFee } from './display-cdp-transaction-fee';
import { DisplayExchangeFeeTransactionFee } from '@tectonics/look-and-feel/volcanes/organisms/side-display/display-exchange-fee-transaction-fee';
import {DisplayIndianGst} from './display-indian-gst';
import { DisplayArgentineEndUserFee } from '@tectonics/look-and-feel/volcanes/organisms/side-display/display-argentine-end-user-fee';

export function SideDisplay(): Node {
  const incomingPayment = useIncomingPayment();
  const allFeatures = useAllFeatures();

  const receipt = useCurrentReceipt();

  const decimalFormat = getMoneyFormat(incomingPayment.country);
  const formatAmount = amountToCurrencyString(decimalFormat);
  const formatFx = fxToCurrencyString(decimalFormat);
  const merchant = getMerchant(incomingPayment);

  return (
    <SidePanel>
      <MerchantLogoDisplay
        logoSrc={getMerchantLogo(merchant)}
        fallbackText={getMerchantName(merchant)}
      />

      <Separator />

      <SideDisplayValues>

        <IfFeature allFeatures={allFeatures} wanted="display_receipt_original_amount">
          <SideDisplayValueItem data-testid="display_original_amount">
            <TotalAmountMessage />
            <SideDisplayValueItemValue>
              {formatAmount(getReceiptAmountExt(receipt))}
            </SideDisplayValueItemValue>
          </SideDisplayValueItem>
        </IfFeature>

        <IfFeature allFeatures={allFeatures} wanted="display_exchange_fee_transaction_fee">
          <DisplayExchangeFeeTransactionFee />
        </IfFeature>

        <IfFeature allFeatures={allFeatures} wanted="display_receipt_fx_rate">
          <SideDisplayValueItem data-testid="display_fx_rate">
            <SideDisplayValueItemTitle>
              <FxMessage />
            </SideDisplayValueItemTitle>
            <SideDisplayValueItemValue>
              {formatFx(getReceiptFx(receipt))}
            </SideDisplayValueItemValue>
          </SideDisplayValueItem>
        </IfFeature>

        <IfFeature allFeatures={allFeatures} wanted="display_receipt_brazilian_tax_iof">
          <DisplayBrazilianIOF />
        </IfFeature>

        <IfFeature allFeatures={allFeatures} wanted="display_cdp_transaction_fee">
          <DisplayCDPTransactionFee />
        </IfFeature>

        <IfFeature allFeatures={allFeatures} wanted="display_receipt_argentine_tax_iva">
          <DisplayArgentineIVA />
        </IfFeature>

        <IfFeature allFeatures={allFeatures} wanted="display_receipt_argentine_tax_ip">
          <DisplayArgentineIP />
        </IfFeature>

        <IfFeature allFeatures={allFeatures} wanted="display_receipt_argentine_tax_ig_ibp">
          <DisplayArgentineIGIBP />
        </IfFeature>

        <IfFeature allFeatures={allFeatures} wanted="display_receipt_argentine_tax_non_resident_igbp">
          <DisplayArgentineNonResidentIgbp />
        </IfFeature>

        <IfFeature allFeatures={allFeatures} wanted="display_receipt_argentine_tax_iibb">
          <DisplayArgentineIIBB />
        </IfFeature>

        <IfFeature allFeatures={allFeatures} wanted="display_argentine_end_user_fee">
          <DisplayArgentineEndUserFee />
        </IfFeature>

        <IfFeature allFeatures={allFeatures} wanted="display_receipt_paraguayan_tax_iva">
          <DisplayParaguayanIVA />
        </IfFeature>

        <IfFeature allFeatures={allFeatures} wanted="display_receipt_costa_rican_tax_iva">
          <DisplayCostaRicanIVA />
        </IfFeature>

        <IfFeature allFeatures={allFeatures} wanted="display_receipt_el_salvadorian_tax_itbms">
          <DisplayElSalvadorianItbms />
        </IfFeature>

        <IfFeature allFeatures={allFeatures} wanted="display_receipt_indian_tax_gst">
          <DisplayIndianGst />
        </IfFeature>

        <IfFeature allFeatures={allFeatures} wanted="display_chilean_rounding_disclaimer">
          <DisplayChileanRoundingDisclaimer />
        </IfFeature>

      </SideDisplayValues>

      <IfFeature allFeatures={allFeatures} wanted="display_selected_instalment">
        <DisplaySelectedInstalment />
      </IfFeature>

      <Separator />

      <IfFeature allFeatures={allFeatures} wanted="display_receipt_final_amount">
        <SideDisplayTotal data-testid="display_local_total">
          <SideDisplayTotalTitle>{translate('volcanes.total')}</SideDisplayTotalTitle>
          <SideDisplayTotalValue>
            {formatAmount(getReceiptAmountLocalTotal(receipt))}
          </SideDisplayTotalValue>
        </SideDisplayTotal>
      </IfFeature>

      <IfFeature allFeatures={allFeatures} wanted="display_paying_with_ebanx">
        <DisplayPayingWithEbanx />
      </IfFeature>
    </SidePanel>
  );
}

function FxMessage(): Node {
  const incomingPayment = useIncomingPayment();
  const country = getCountry(incomingPayment);

  switch (country) {
    case 'br':
      return <p>Taxa de Câmbio</p>;
    case 'ar':
    case 'bo':
    case 'cl':
    case 'co':
    case 'ec':
    case 'mx':
    case 'pe':
    case 'uy':
    case 'cr':
    case 'py':
    case 'pa':
    case 'gt':
    case 'sv':
    case 'do':
      return <p>Tipo de Cambio</p>;
    case 'za':
    case 'ke':
    case 'ng':
    case 'in':
    case 'tz':
    case 'ug':
    case 'zm':
    case 'eg':
    case 'gh':
    case 'jm':
      return <p>Exchange Rate</p>;
    default:
      throw new Error(`No message found for fx message in country ${country}`);
  }
}

function TotalAmountMessage(): Node {
  const incomingPayment = useIncomingPayment();
  const country = getCountry(incomingPayment);

  switch (country) {
    case 'eg':
      return <SideDisplayValueItemTitle>{translate('volcanes.amount')}</SideDisplayValueItemTitle>;

    case 'za':
    case 'ke':
    case 'ng':
    case 'in':
    case 'tz':
    case 'ug':
    case 'zm':
    case 'gh':
    case 'jm':
      return <SideDisplayValueItemTitle>Amount</SideDisplayValueItemTitle>;

    default:
      return <SideDisplayValueItemTitle>Valor</SideDisplayValueItemTitle>;
  }
}
